import { Box, Skeleton, Typography } from '@mui/material';
import { useAlertStatus } from 'customHook/useAlertStatus';
import { formatDealAmount } from 'helper/short-currency';
import { PIPELINE_MAP } from 'model/deals.model';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';
import { DealsDetails } from '../DealsDetails';
import { FilterState } from '../DealsFilters/DealsFilters.types';
import { filtersToQueryParams } from '../DealsFilters/DealsFilters.utils';
import { DealsSettings } from '../DealsSettings';
import { NewDealForm } from '../NewDealForm/NewDealForm';
import { DealCard } from './DealCard';

type KanbanColumn = {
  id: 'early' | 'develop' | 'active' | 'closing';
  label: string;
  color: string;
};

const KanbanBoardData: KanbanColumn[] = [
  {
    label: 'Early',
    color: zeplinColor.Neutral8,
    id: 'early',
  },
  {
    label: 'Develop',
    color: zeplinColor.AlertInfo,
    id: 'develop',
  },
  {
    label: 'Active',
    color: zeplinColor.Blue,
    id: 'active',
  },
  {
    label: 'Closing',
    color: zeplinColor.Success,
    id: 'closing',
  },
];

const calculateColumnEarnings = (deals: any[]): number => {
  if (!deals || deals.length === 0) return 0;

  const sum = deals.reduce((total, deal) => {
    const amount = deal.amount || deal.value || deal.dealValue || 0;
    const numericAmount = typeof amount === 'number' ? amount : parseFloat(String(amount)) || 0;
    return total + numericAmount;
  }, 0);

  return Math.round(sum * 10) / 10;
};

type DealKanbanObject = {
  [k in 'early' | 'develop' | 'active' | 'closing']: any[];
};

type KanbanColumnId = 'early' | 'develop' | 'active' | 'closing';

const KANBAN_LIMIT = 50;

interface DealsKanbanProps {
  filterState: FilterState;
  selectedPipelines: string[];
  tabValue: number;
  searchTerm?: string;
  needsAttention?: boolean;
}

export const DealsKanban = ({
  filterState,
  selectedPipelines,
  tabValue,
  searchTerm = '',
  needsAttention = false,
}: DealsKanbanProps) => {
  const [deals, setDeals] = useState<DealKanbanObject>();
  const [loading, setLoading] = useState(true);
  const [kanbanPage, setKanbanPage] = useState(1);
  const [kanbanHasMore, setKanbanHasMore] = useState(true);
  const [kanbanLoading, setKanbanLoading] = useState(false);
  const [totalCounts, setTotalCounts] = useState<Record<KanbanColumnId, number>>({
    early: 0,
    develop: 0,
    active: 0,
    closing: 0,
  });
  const [dealCardElement, setDealCardElement] = useState<SVGSVGElement | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<any>(null);
  const [selectedDealForDetails, setSelectedDealForDetails] = useState<any>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [selectedDealForEdit, setSelectedDealForEdit] = useState<any>(null);

  const isDetailsOpenRef = useRef(isDetailsOpen);
  const selectedDealForDetailsRef = useRef(selectedDealForDetails);

  useEffect(() => {
    isDetailsOpenRef.current = isDetailsOpen;
    selectedDealForDetailsRef.current = selectedDealForDetails;
  }, [isDetailsOpen, selectedDealForDetails]);

  const normalizeDealData = (deal: any): any => {
    let normalizedPipelineType = deal.pipelineType || deal.dealType || '';
    if (normalizedPipelineType && PIPELINE_MAP[normalizedPipelineType]) {
      normalizedPipelineType = PIPELINE_MAP[normalizedPipelineType];
    }

    const dealId = deal.id || deal.dealId || '';

    return {
      ...deal,
      id: dealId,
      dealId: dealId,
      name: deal.name || deal.title || '',
      clientName: deal.clientName || deal.client || '',
      ownerName: deal.ownerName || deal.owner || '',
      pipelineType: normalizedPipelineType,
      stageName: deal.stageName || deal.stage || deal.dealStageName || '',
      amount: deal.amount || deal.value || deal.dealValue || 0,
      closeDate: deal.closeDate || '',
      profilePicture: deal.profilePicture || deal.avatar || '',
    };
  };

  const getDeals = async (pageToFetch: number = 1, reset: boolean = false) => {
    if (kanbanLoading && !reset) return;

    try {
      if (reset) {
        setLoading(true);
        setKanbanPage(1);
        setKanbanHasMore(true);
      } else {
        setKanbanLoading(true);
      }

      const queryParams = filtersToQueryParams(
        filterState,
        selectedPipelines,
        tabValue,
        searchTerm,
        needsAttention,
      );

      // Add pagination parameters
      queryParams.page = pageToFetch;
      queryParams.limit = KANBAN_LIMIT;

      const data = await DealsService.getKanbanView(queryParams);
      const rawDeals = data?.data || data;
      const total = data?.total || data?.count || 0;

      if (rawDeals && typeof rawDeals === 'object') {
        const normalizedDeals: DealKanbanObject = {
          early: [],
          develop: [],
          active: [],
          closing: [],
        };

        Object.keys(rawDeals).forEach((key) => {
          if (key in normalizedDeals) {
            const columnDeals = rawDeals[key] || [];
            normalizedDeals[key as KanbanColumnId] = columnDeals.map(normalizeDealData);
          }
        });

        if (reset || pageToFetch === 1) {
          // Reset deals on first page or reset
          setDeals(normalizedDeals);

          // Update total counts if provided
          if (data?.totals && typeof data.totals === 'object') {
            setTotalCounts({
              early: data.totals.early || 0,
              develop: data.totals.develop || 0,
              active: data.totals.active || 0,
              closing: data.totals.closing || 0,
            });
          } else {
            // Calculate totals from current data
            setTotalCounts({
              early: normalizedDeals.early.length,
              develop: normalizedDeals.develop.length,
              active: normalizedDeals.active.length,
              closing: normalizedDeals.closing.length,
            });
          }
        } else {
          // Append deals for pagination
          setDeals((prev) => {
            if (!prev) return normalizedDeals;
            return {
              early: [...prev.early, ...normalizedDeals.early],
              develop: [...prev.develop, ...normalizedDeals.develop],
              active: [...prev.active, ...normalizedDeals.active],
              closing: [...prev.closing, ...normalizedDeals.closing],
            };
          });
        }

        // Update pagination state
        setKanbanPage(pageToFetch);

        // Check if there are more deals to load
        // Calculate total deals across all columns after this fetch
        const totalDealsAfterFetch =
          reset || pageToFetch === 1
            ? Object.values(normalizedDeals).reduce((sum, col) => sum + col.length, 0)
            : (deals ? Object.values(deals).reduce((sum, col) => sum + col.length, 0) : 0) +
              Object.values(normalizedDeals).reduce((sum, col) => sum + col.length, 0);

        // If total is provided, use it; otherwise check if we got a full page of deals
        // (if we got fewer deals than the limit, we've reached the end)
        const hasMore = total
          ? totalDealsAfterFetch < total
          : totalDealsAfterFetch >= pageToFetch * KANBAN_LIMIT;
        setKanbanHasMore(hasMore);

        if (isDetailsOpenRef.current && selectedDealForDetailsRef.current) {
          const dealId =
            selectedDealForDetailsRef.current.id || selectedDealForDetailsRef.current.dealId;
          if (dealId) {
            const allDeals = [
              ...(reset || pageToFetch === 1 ? normalizedDeals.early : deals?.early || []),
              ...(reset || pageToFetch === 1 ? normalizedDeals.develop : deals?.develop || []),
              ...(reset || pageToFetch === 1 ? normalizedDeals.active : deals?.active || []),
              ...(reset || pageToFetch === 1 ? normalizedDeals.closing : deals?.closing || []),
            ];
            const updatedDeal = allDeals.find((deal) => (deal.id || deal.dealId) === dealId);
            if (updatedDeal) {
              setSelectedDealForDetails(updatedDeal);
            }
          }
        }
      } else {
        if (reset || pageToFetch === 1) {
          setDeals(rawDeals);
        }
        setKanbanHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
      if (reset || pageToFetch === 1) {
        setDeals(undefined);
      }
      setKanbanHasMore(false);
    } finally {
      setLoading(false);
      setKanbanLoading(false);
    }
  };

  const fetchMoreDeals = async () => {
    if (kanbanLoading || !kanbanHasMore) return;
    await getDeals(kanbanPage + 1, false);
  };

  useEffect(() => {
    getDeals(1, true);
  }, [filterState, selectedPipelines, tabValue, searchTerm, needsAttention]);

  const handleOpenSettings = (event: MouseEvent<SVGSVGElement>, deal: any) => {
    event.stopPropagation();
    setDealCardElement(event.currentTarget);
    setSelectedDeal(deal);
  };

  const { startOperation, completeOperation } = useAlertStatus();

  const handleViewDetails = () => {
    if (selectedDeal) {
      setSelectedDealForDetails(selectedDeal);
      setIsDetailsOpen(true);
    }
  };

  const handleCardClick = (deal: any) => {
    setSelectedDealForDetails(deal);
    setIsDetailsOpen(true);
  };

  const handleEditFromSettings = () => {
    if (selectedDeal) {
      setSelectedDealForEdit(selectedDeal);
      setOpenEditForm(true);
    }
  };

  const handleStageChange = () => {
    getDeals(1, true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedDealForDetails(null);
  };

  const handleEditDeal = () => {
    setIsDetailsOpen(false);
    setSelectedDealForEdit(selectedDealForDetails);
    setOpenEditForm(true);
  };

  function handleOnDrag(e: React.DragEvent, dealTitle: string, sourceColumn: string) {
    e.dataTransfer?.setData('dealTitle', dealTitle);
    e.dataTransfer?.setData('sourceColumn', sourceColumn);
  }

  function handleOnDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  async function handleOnDrop(e: React.DragEvent, targetColumn: KanbanColumnId) {
    const dealTitle = e.dataTransfer.getData('dealTitle');
    const sourceColumn = e.dataTransfer.getData('sourceColumn') as KanbanColumnId;

    if (sourceColumn === targetColumn) return;

    const prevDeals = deals;
    if (!prevDeals) return;

    const dealToMove = prevDeals[sourceColumn].find((d: any) => (d.title || d.name) === dealTitle);
    if (!dealToMove) return;

    const dealId = dealToMove.id || dealToMove.dealId;

    if (!dealId) {
      completeOperation('update', 'Deal ID not found', true);
      return;
    }

    setDeals((prev) => {
      if (!prev) return prev;

      const newSourceList = prev[sourceColumn].filter(
        (d: any) => (d.title || d.name) !== dealTitle,
      );
      const newTargetList = [...prev[targetColumn], { ...dealToMove, bucket: targetColumn }];

      return {
        ...prev,
        [sourceColumn]: newSourceList,
        [targetColumn]: newTargetList,
      } as DealKanbanObject;
    });

    try {
      startOperation('update', 'Updating deal stage...');

      const payload = {
        dealStageName: targetColumn,
      };

      console.log('Updating deal:', { dealId, payload, targetColumn });

      const response = await DealsService.updateDeal(dealId, payload);
      console.log('Update response:', response);

      await getDeals(1, true);

      completeOperation('update', 'Deal stage updated successfully');
    } catch (error: any) {
      console.error('Error updating deal:', error);
      setDeals(prevDeals);
      completeOperation(
        'update',
        error?.response?.data?.message || 'Failed to update deal stage',
        true,
      );
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          gap: '24px',
        }}
      >
        {KanbanBoardData.map(({ id, label }) => (
          <Box key={id} sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px 8px 0px',
                marginBottom: '8px',
              }}
            >
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <Skeleton variant='circular' width={8} height={8} />
                <Skeleton variant='text' width={100} height={24} />
              </Box>
              <Skeleton variant='text' width={60} height={20} />
            </Box>
            <Box
              sx={{
                height: 'calc(100vh - 360px)',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {[1, 2, 3].map((index) => (
                <Skeleton
                  key={index}
                  variant='rounded'
                  height={180}
                  sx={{ borderRadius: '16px' }}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (!deals) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <Typography>No deals found</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        flex: 1,
        gap: '24px',
      }}
    >
      {KanbanBoardData.map(({ id, label, color }) => {
        const columnDeals = deals[id] || [];
        const earnings = formatDealAmount(calculateColumnEarnings(columnDeals));

        return (
          <Box
            key={id}
            sx={{
              width: '100%',
            }}
            onDragOver={handleOnDragOver}
            onDrop={(e) => handleOnDrop(e, id)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px 8px 0px',
                marginBottom: '8px',
              }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '100%',
                    backgroundColor: color,
                  }}
                />
                <Typography
                  variant='heading3'
                  sx={{
                    color: zeplinColor.BasicBlack,
                  }}
                >
                  {label} ({totalCounts[id] > 0 ? totalCounts[id] : columnDeals.length})
                </Typography>
              </Box>
              <Typography
                variant='body3'
                sx={{
                  color: zeplinColor.Neutral8,
                }}
              >
                ${earnings}
              </Typography>
            </Box>
            <Box
              id={`kanban-column-${id}`}
              sx={{
                height: 'calc(100vh - 400px)',
                overflowY: 'auto',
              }}
            >
              {columnDeals.length === 0 && !loading && !kanbanLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '24px',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      color: zeplinColor.Neutral8,
                      textAlign: 'center',
                    }}
                  >
                    No deals in this stage
                  </Typography>
                </Box>
              ) : (
                <InfiniteScroll
                  dataLength={columnDeals.length}
                  next={fetchMoreDeals}
                  hasMore={kanbanHasMore && !kanbanLoading}
                  loader={
                    kanbanLoading ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          padding: '16px',
                        }}
                      >
                        <Skeleton
                          variant='rounded'
                          height={180}
                          sx={{ borderRadius: '16px', width: '100%' }}
                        />
                      </Box>
                    ) : null
                  }
                  scrollableTarget={`kanban-column-${id}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                  }}
                >
                  {columnDeals.map((deal) => (
                    <DealCard
                      key={deal.dealId}
                      data={deal}
                      onDrag={(e) => handleOnDrag(e, deal.title || deal.name, id)}
                      onSettingsClick={handleOpenSettings}
                      getDeals={() => getDeals(1, true)}
                      onCardClick={handleCardClick}
                    />
                  ))}
                </InfiniteScroll>
              )}
            </Box>
          </Box>
        );
      })}

      <DealsSettings
        anchorElement={dealCardElement}
        onClose={() => {
          setDealCardElement(null);
          setSelectedDeal(null);
        }}
        startOperation={startOperation}
        completeOperation={completeOperation}
        deal={selectedDeal}
        onDealDeleted={() => getDeals(1, true)}
        onViewDetails={handleViewDetails}
        onEdit={handleEditFromSettings}
        onStageChange={handleStageChange}
      />
      {isDetailsOpen && selectedDealForDetails && (
        <DealsDetails
          isOpen={isDetailsOpen}
          deal={selectedDealForDetails}
          onClose={handleCloseDetails}
          handleEdit={handleEditDeal}
          onDealDeleted={() => {
            handleCloseDetails();
            getDeals(1, true);
          }}
          getDeals={() => getDeals(1, true)}
        />
      )}
      {openEditForm && selectedDealForEdit && (
        <NewDealForm
          isEdit
          dealId={selectedDealForEdit.id || selectedDealForEdit.dealId}
          deal={selectedDealForEdit}
          onDealCreated={(changesMade: boolean = true) => {
            setOpenEditForm(false);
            setSelectedDealForEdit(null);
            setDealCardElement(null);
            setSelectedDeal(null);
            if (changesMade) {
              getDeals(1, true);
            }
          }}
        />
      )}
    </Box>
  );
};
